import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PeriodKey } from '../store/admin/reviews/reviewsApi';
import { fetchReviewsReport, revokeLastDownloads, downloadReviewsCsv, downloadReviewsXlsx } from '../store/admin/reviews/reviews.slice';
import { AppDispatch, RootState } from '../app/store';


/** Simple pagination helper */
function paginate<T>(items: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export default function DownloadReviewsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();

  // local UI state
  const [period, setPeriod] = useState<PeriodKey>('last_90_days');
  const [minScore, setMinScore] = useState<number | null>(null); // null = all
  const [page, setPage] = useState(1);
  const pageSize = 5;

  // redux state
  const { report, loading, error } = useSelector((s: RootState) => s.reviews);

  // rows for table
  const rows = report?.reviews ?? [];

  // optional local filtering (keep same UX as before)
  const filtered = useMemo(
    () => (minScore ? rows.filter((r) => (r.score ?? 0) >= minScore) : rows),
    [rows, minScore]
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = paginate(filtered, page, pageSize);

  // fetch on open/filters change
  useEffect(() => {
    if (!open) return;
    setPage(1);
    dispatch(
      fetchReviewsReport({
        period,
        minScore,
        // include 'sinceLastReport' if you want Summary sheet to show the "new since" metric
        // sinceLastReport: true,
      })
    );
  }, [open, period, minScore, dispatch]);

  // revoke any previous blob urls when unmounting modal (nice-to-have)
  useEffect(() => {
    if (!open) return;
    return () => {
      dispatch(revokeLastDownloads());
    };
  }, [open, dispatch]);

  async function handleDownloadCsv() {
    try {
      const url = await dispatch(
        downloadReviewsCsv({ period, minScore })
      ).unwrap();
      const a = document.createElement('a');
      a.href = url;
      a.download = `oeldv-reviews-${period}.csv`;
      a.click();
      // do not revoke immediately; let the browser download first
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert('Could not download CSV.');
    }
  }

  async function handleDownloadXlsx() {
    try {
      const url = await dispatch(
        downloadReviewsXlsx({ period, minScore, sinceLastReport: true })
      ).unwrap();
      const a = document.createElement('a');
      a.href = url;
      a.download = `oeldv-reviews-${period}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      alert('Could not download Excel.');
    }
  }

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/50'
        onClick={onClose}
        aria-hidden='true'
      />
      {/* Modal */}
      <div
        role='dialog'
        aria-modal='true'
        className='relative w-[880px] max-w-[95vw] rounded-2xl bg-white p-5 shadow-2xl dark:bg-neutral-900'
      >
        {/* Header */}
        <div className='flex items-center justify-between'>
          <h2 className='text-lg font-semibold text-neutral-900 dark:text-neutral-100'>
            Download Reviews
          </h2>
          <button
            onClick={onClose}
            className='inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800'
            aria-label='Close'
          >
            ✕
          </button>
        </div>

        {/* Filters */}
        <div className='mt-4 rounded-xl border border-neutral-200 p-4 dark:border-neutral-800'>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            {/* Score */}
            <div className='flex flex-col'>
              <label className='mb-2 text-sm font-medium text-neutral-600 dark:text-neutral-300'>
                Score
              </label>
              <select
                className='w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'
                value={minScore ?? ''}
                onChange={(e) =>
                  setMinScore(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value=''>All Scores</option>
                <option value='5'>≥5</option>
                <option value='4'>≥4</option>
                <option value='3'>≥3</option>
                <option value='2'>≥2</option>
                <option value='1'>≥1</option>
              </select>
            </div>

            {/* Period */}
            <div className='flex flex-col'>
              <label className='mb-2 text-sm font-medium text-neutral-600 dark:text-neutral-300'>
                Period
              </label>
              <div className='relative'>
                <select
                  className='w-full appearance-none rounded-lg border border-neutral-300 bg-white px-3 py-2 pr-10 text-sm outline-none transition focus:border-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as PeriodKey)}
                >
                  <option value='last_7_days'>Last 7 days</option>
                  <option value='last_30_days'>Last 30 days</option>
                  <option value='last_90_days'>Last 90 days</option>
                </select>
                <span className='pointer-events-none absolute inset-y-0 right-3 inline-flex items-center text-neutral-400'>
                  📅
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className='mt-4 rounded-xl border border-neutral-200 dark:border-neutral-800'>
          <div className='grid grid-cols-[80px_1fr_210px] items-center gap-3 border-b border-neutral-200 px-4 py-3 text-xs font-semibold text-neutral-500 dark:border-neutral-800 dark:text-neutral-400'>
            <div>Score</div>
            <div>Guest Review Comments.</div>
            <div className='text-right sm:text-left'>Source</div>
          </div>

          <div className='max-h-[360px] overflow-y-auto'>
            {loading ? (
              <div className='p-6 text-sm text-neutral-500 dark:text-neutral-400'>
                Loading…
              </div>
            ) : error ? (
              <div className='p-6 text-sm text-red-600'>{error}</div>
            ) : filtered.length === 0 ? (
              <div className='p-6 text-sm text-neutral-500 dark:text-neutral-400'>
                No reviews found for the selected filters.
              </div>
            ) : (
              pageRows.map((r, idx) => (
                <div
                  key={idx}
                  className='grid grid-cols-[80px_1fr_210px] items-start gap-3 border-t border-neutral-100 px-4 py-3 text-sm dark:border-neutral-800'
                >
                  <div className='pt-1 text-neutral-800 dark:text-neutral-100'>
                    {r.score ?? '—'}
                  </div>
                  <div className='text-neutral-700 dark:text-neutral-200'>
                    {r.text}
                  </div>
                  <div className='truncate text-right sm:text-left'>
                    {r.url ? (
                      <a
                        href={r.url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-blue-600 underline underline-offset-2 hover:text-blue-700 dark:text-blue-400'
                        title={r.url}
                      >
                        {r.url}
                      </a>
                    ) : (
                      <span className='text-neutral-400'>—</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className='mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row'>
          {/* Pagination */}
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className='inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-600 disabled:opacity-40 dark:border-neutral-700 dark:text-neutral-200'
              aria-label='Previous page'
            >
              ‹
            </button>
            <span className='inline-flex h-8 min-w-[2.25rem] items-center justify-center rounded-lg border border-neutral-300 px-2 text-sm dark:border-neutral-700 dark:text-neutral-100'>
              {page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              disabled={page === pageCount}
              className='inline-flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-600 disabled:opacity-40 dark:border-neutral-700 dark:text-neutral-200'
              aria-label='Next page'
            >
              ›
            </button>
          </div>

          {/* Download buttons */}
          <div className='flex items-center gap-2'>
            <button
              onClick={handleDownloadCsv}
              className='inline-flex items-center justify-center rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800/60'
              disabled={loading || filtered.length === 0}
              title='Download CSV'
            >
              Download CSV
            </button>
            <button
              onClick={handleDownloadXlsx}
              className='inline-flex items-center justify-center rounded-lg bg-neutral-900 px-5 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-50 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white'
              disabled={loading || filtered.length === 0}
              title='Download Excel (.xlsx)'
            >
              Download Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
